/**
 * Icon mappings for semantic steps in the history timeline.
 * Re-exports Radix UI icons with semantic names.
 */

import { CheckIcon, PlayIcon, StopIcon, TargetIcon } from '@radix-ui/react-icons';
import _ from 'lodash';
import React, { memo } from 'react';

import { cn } from '~/utils/styles';

interface IconProps {
  className?: string;
}

interface MiniHorseIconProps {
  color: string; // Hex color value like '#3aaa35'
  className?: string;
}

/**
 * Miniature horse icon for timeline nodes.
 * Simplified version of HorseIcon without tooltip, animations, or overlays.
 * Renders a colored circle with white horse silhouette.
 */
export const MiniHorseIcon: React.FC<MiniHorseIconProps> = memo(
  ({ color, className }) => (
    <svg baseProfile="basic" viewBox="0 0 500 500" className={cn('h-6 w-6', className)}>
      <circle cx={250} cy={250} r={250} fillRule="evenodd" clipRule="evenodd" fill={color} />
      <path
        d="M341.2 76.4c-15.2-3.1-27.7 2.5-26.7 19-1.9.3-3.1 1.9-4.9 2.3-2.6-.2-5.2-.5-7.8-.7-5 .6-9.3 3.8-13.9 5.1-7.5 2.1-14.2 3.4-20.7 7.2-3.6 2.1-5.3 6.8-8.3 10.1-4.5 5.1-15.7 7-16.1 18.9-.2 6.8 2.6 13.1 5.9 17.2 2.3 2.9 5.7 4 5.3 9.2-2 2.3-3.4 7.9-4.6 11.1-.2 0-.4-.1-.6-.1-3.5-4.5-13-4.6-17.6-8-6.3-4.6-12.9-10.3-20.9-14-10.4-4.8-29.3 8.3-37.2 11.7-8.2 3.5-34.8 3.8-45.7 2-17.7-3-31.6-9.8-48.6-9.8-14.8 0-17.5-4.8-30.1 1.2-2.3 1.1-5.1 1.8-6.9 3.8 1.7 6.1 1 13.3 2.6 19.5 1.3 5.2 5.4 8.5 8.3 12.7 1.5 2.3 1 5 1.8 7.9l9.1 13.1c7.7 6.8 21 2.3 28.1.3 3.7-.4 7.5-.8 11.2-1.1.7.4 1.5.8 2.2 1.2 1.4-.4 3.8-3.1 5.5-3.6 10.8-3.3 24.2-8.1 36.5-6.5-.1.5-.3 1-.4 1.4 1.5 5.7-1 13.7.6 19.2 3.1 10.4 12 21.3 13.4 33.1 1 8.4-7 26.7-4.6 32.1 2.3 8.8 11.4 12.7 15.9 19.8 4.4 6.8.5 21.1 5.1 28 4.9 7.4 11.7 12 17 19.2 2.8 3.8 2.4 12.2 4 17.2 2.6 5.4 5.2 10.9 7.8 16.4 1.8 2.6 4.5 3.5 5.7 6.7 1.1 3.4-1.6 10.5-.6 14.3 3.2 7.2 26.7 13.5 33.8 8 2.7-2.8 4.2-8.4 5.3-14.5-2.8-3.9-2.7-10-4.4-15-2.7-8.1-12.1-20.3-11.9-29.7 0 0 1.5-26.2 1-27.4-1-2.5-9.9-6.4-12.5-8.3-5.2-3.9-23.9-25.4-23-33-.8-2.8 1.6-7.5 1.8-11.3.6-11.9 1.6-30.7 12.3-33.2 7.4-4.4 19.8 1.3 22 7.8 1 3 .4 6.1 2.2 8.3 3.2 3.9 9.2 8 13.5 10.4 4.2.7 8.3 1.5 12.5 2.2 3 1.9 4.5 6.8 6.8 9.3 3.1 3.4 9.4 2.5 13.2 2.3.9-4.8 2.6-7.2 5.8-7.9 3.3-2.3 8.1 1 12.7.6 13.2-1 28.9 10.3 29.5 23.9-2.3-.4-4.6-.8-7-1.2-2.1-2.2-5.9-.1-9.5-1.7-12.6-5.6-18.5 5.9-29 7.4-1.3-3.3-5.3-9.6-8-11.4-3.5 2.5-7.8 3.1-11.3 5.5-5.1 3.4-8.5 11.2-13.7 14.5.2.3.3.6.5.9 4.7 12.5 23.6 14.5 39 17.2 5.7 1 12.6 3.4 17.6 2.2 2.7-.7 3.3-4.1 5.3-5.1 1.9-.9 8.4-1.4 10.1-1.7 7.7-1.2 14.2 0 22.8 1.5 2.3-3.4 6.4-4.5 8.7-7.8.9-2.1 1.7-4.2 2.6-6.3 1.5.3 3 .5 4.5.8 1.1 1.5 3.1 2.5 4.9 3.4-.1.5-.1 1.1-.2 1.7-.2 0-.4-.1-.6-.1-3.8 5.3-9.8 6.6-14.8 10.1-4.7 3.3-8.1 13.6-12.5 16.8-2 1.5-4.4.5-6.8 1.4-4.1 1.5-9.4 4-13.5 6.4-6.5 3.8-10.7 11.9-17 15.9.2.3.4.7.5 1 3.4 4.8 8.9 6.2 12.9 10.5 6.3 6.8 6.6 20.2 17 23.5 6-4.4 9.4-1.1 10.6-14.9 1.8-3 1.6-9.6 3.4-12.2 3.5-3.2 8.6-3 11.5-7 7.6-10.2 14.9-21.5 24-28.4 6.8-5.1 13.8-8.6 15-22.9-3.3-6.9-3.5-14.1-8-19.6-7.5-9-28.8-28.4-28.9-41.7 4.9-7.4 1.8-21.6 5.3-31.7 3.2-9.3 10.2-11.1 15.1-17.2 3-3.7 5.5-10 9.5-12 1.8.3 3.5.6 5.3.9 2.5 2.4 6.3 1.5 9.9 3.1 4.5 2.1 10.4 4.5 14.4 6.8 3.8 2.1 9.8 1.3 13 3 2 1.1 10 1.8 10.1 1.7 10-1.9 11.9-26.2 7.2-37.8-.1-.3-9.7-11.9-10.3-12.5-2.2-2.1-5.1-2.5-7.2-4.6-1.2-1.8-2.5-3.5-3.7-5.3-4.1-4.3-8.4-6.8-13-10.7-1.7-2.7-3.4-5.3-5.1-8-1.4-1.4-2.7-1.3-4.3-2.7-2.2-1.9-3.9-6-6.1-8.1-2.7-2.6-14.5-9.2-18.6-6.3-5.6 2.9-9.7 9.2-15.5 12-3.7 1.8-7.6 1.3-11.7 2.5-4.8 1.4-11.4 7.1-14.7 11.3-.2-.2-.3-.5-.5-.7-6.7-6.5-11.6-12-12-24.7.4.1.7.1 1.1.2 2-1.2 5.1-2 7.7-1.9.7 1.1 2.5 3 3.1 2.4 3.4-3 5.4-7.6 8.3-11.2 6.2-7.7 10-5.2 14-18-6.2-2.8-8.9-9-13.3-13.1-3.1-1.8-6.3-3.7-9.5-5.7z"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="#fff"
      />
    </svg>
  ),
  (prevProps, nextProps) =>
    prevProps.color === nextProps.color && prevProps.className === nextProps.className,
);

MiniHorseIcon.displayName = 'MiniHorseIcon';

/**
 * Icon for SELECTMOVEOPP (horse moved).
 * Uses TargetIcon from Radix UI.
 */
export const HorseIcon: React.FC<IconProps> = ({ className = 'w-3.5 h-3.5' }) => (
  <TargetIcon className={className} />
);

/**
 * Icon for STARTROUND (round started).
 * Uses PlayIcon from Radix UI.
 */
export const FlagIcon: React.FC<IconProps> = ({ className = 'w-3.5 h-3.5' }) => (
  <PlayIcon className={className} />
);

/**
 * Icon for ENDROUND (round ended).
 * Uses CheckIcon from Radix UI.
 */
export const CheckeredFlagIcon: React.FC<IconProps> = ({ className = 'w-3.5 h-3.5' }) => (
  <CheckIcon className={className} />
);

/**
 * Icon for ENDGAME (game ended).
 * Uses StopIcon from Radix UI.
 */
export const TrophyIcon: React.FC<IconProps> = ({ className = 'w-3.5 h-3.5' }) => (
  <StopIcon className={className} />
);

/** Plus shape from bottom bar bonus card (CardBonus.tsx), centered in 0 0 42 42. */
const PLUS_POINTS = '42,20 22,20 22,0 20,0 20,20 0,20 0,22 20,22 20,42 22,42 22,22 42,22';

/** Card value for mini icon: 0 = bonus, 3–12 = number, -12…-9 = joker. null = generic card. */
export type MiniCardValue = number | null;

interface MiniCardIconProps {
  /** Card value (0, 3–12, -12…-9) or null for generic card. */
  value: MiniCardValue;
  color: string;
  className?: string;
  /** When true, card was played but handicap exceeded (horse did not move); show muted style + "0" badge. */
  handicapExceeded?: boolean;
  /** When true, show only the card outline (no number/symbol inside). Use for turn-type prefix. */
  empty?: boolean;
}

/**
 * Miniature card icon for timeline (card-play moves).
 * Portrait card with value: number, J for joker, or plus icon for bonus (same as bottom bar).
 */
/** Two-digit card values use smaller font so they fit with padding inside the mini card. */
const CARD_FONT_SIZE_SINGLE = 9;
const CARD_FONT_SIZE_DOUBLE = 7;

export const MiniCardIcon: React.FC<MiniCardIconProps> = memo(
  ({ value, color, className, handicapExceeded = false, empty = false }) => {
    const isBonus = value === 0 && !empty;
    const isJoker = value !== null && value < 0;
    // For joker cards, show the actual positive value instead of "J"
    const displayValue = isJoker ? Math.abs(value) : value;
    const label = empty
      ? null
      : displayValue === null
        ? '?'
        : isBonus
          ? null
          : String(displayValue);
    const isTwoDigit = typeof displayValue === 'number' && displayValue >= 10 && displayValue <= 12;
    const fontSize = isTwoDigit ? CARD_FONT_SIZE_DOUBLE : CARD_FONT_SIZE_SINGLE;
    // Special styling for joker cards: use dashed border and slightly different color treatment
    const jokerStrokeColor = isJoker ? '#f59e0b' : color; // Amber/gold tint for jokers
    const strokeDashArray = handicapExceeded ? '3 2' : isJoker ? '2 2' : undefined;
    const showInner = !empty && (isBonus || label != null);
    return (
      <svg
        viewBox="0 0 24 24"
        className={cn('h-6 w-6', className, handicapExceeded && 'opacity-90')}
        aria-hidden
      >
        {/* Portrait card shape (narrower than tall) */}
        <rect
          x={6}
          y={2}
          width={12}
          height={20}
          rx={2}
          fill="#fff"
          stroke={jokerStrokeColor}
          strokeWidth={handicapExceeded ? 1.2 : isJoker ? 1.8 : 1.5}
          strokeDasharray={strokeDashArray}
        />
        {showInner &&
          (isBonus ? (
            <g transform="translate(12, 12) scale(0.19) translate(-21, -21)">
              <polygon fill={color} points={PLUS_POINTS} />
            </g>
          ) : (
            <text
              x={12}
              y={12.5}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={jokerStrokeColor}
              fontSize={fontSize}
              fontWeight="bold"
              fontFamily="system-ui, sans-serif"
            >
              {label ?? '?'}
            </text>
          ))}
        {/* Forbidden badge: card played but handicap exceeded (horse did not move) */}
        {handicapExceeded && (
          <g>
            <circle cx={18} cy={20} r={4.5} fill="rgb(180 83 9)" stroke="#fff" strokeWidth={1} />
            <line
              x1={13.8}
              y1={16.2}
              x2={22.2}
              y2={23.8}
              stroke="#fff"
              strokeWidth={1.4}
              strokeLinecap="round"
            />
          </g>
        )}
      </svg>
    );
  },
  (prevProps, nextProps) => _.isMatch(nextProps, prevProps),
);

MiniCardIcon.displayName = 'MiniCardIcon';

interface MiniDiceIconProps {
  /** Dice face value 1–6, or null for generic. */
  value: number | null;
  color: string;
  className?: string;
  /** When true, show diagonal cross-out (unselected die), like top-bar Dice. */
  disabled?: boolean;
}

/** Dice dot positions (viewBox 0 0 24 24), inset from edge so pips don’t touch border: topL, topR, midL, midC, midR, botL, botR */
const DICE_DOTS = [
  { cx: 7, cy: 7 },
  { cx: 17, cy: 7 },
  { cx: 7, cy: 12 },
  { cx: 12, cy: 12 },
  { cx: 17, cy: 12 },
  { cx: 7, cy: 17 },
  { cx: 17, cy: 17 },
] as const;
const DICE_R = 2.5;
/** Which dots to show for each face 1–6 (indices into DICE_DOTS: 0=topL, 1=topR, 2=midL, 3=midC, 4=midR, 5=botL, 6=botR). */
const DICE_FACES: number[][] = [
  [3],
  [0, 6],
  [0, 3, 6],
  [0, 1, 5, 6],
  [0, 1, 3, 5, 6],
  [0, 1, 2, 4, 5, 6],
];

/**
 * Miniature dice icon for timeline (dice-roll moves).
 * Square face with pips inset and dark border so pips don’t melt with the edge.
 */
const disabledOpacity = 0.5;

export const MiniDiceIcon: React.FC<MiniDiceIconProps> = memo(
  ({ value, color, className, disabled = false }) => {
    const faceIndex = value != null && value >= 1 && value <= 6 ? value - 1 : 0;
    const indices = DICE_FACES[faceIndex];
    return (
      <svg
        viewBox="0 0 24 24"
        className={cn('h-6 w-6', className)}
        aria-hidden
        opacity={disabled ? disabledOpacity : 1}
      >
        {/* Dark stroke so white pips stay distinct from the border */}
        <rect
          x={2}
          y={2}
          width={20}
          height={20}
          rx={5}
          fill={color}
          stroke="rgba(0,0,0,0.35)"
          strokeWidth={1.5}
        />
        {indices.map((i) => (
          <circle key={i} cx={DICE_DOTS[i].cx} cy={DICE_DOTS[i].cy} r={DICE_R} fill="#fff" />
        ))}
        {disabled && (
          <>
            <line
              x1={4}
              y1={4}
              x2={20}
              y2={20}
              stroke="#fff"
              strokeWidth={1.5}
              strokeLinecap="round"
            />
            <line
              x1={20}
              y1={4}
              x2={4}
              y2={20}
              stroke="#fff"
              strokeWidth={1.5}
              strokeLinecap="round"
            />
          </>
        )}
      </svg>
    );
  },
  (prevProps, nextProps) => _.isMatch(nextProps, prevProps),
);

MiniDiceIcon.displayName = 'MiniDiceIcon';

interface MiniDicePairIconProps {
  color: string;
  className?: string;
}

/**
 * Two small diagonal dice (no pips). Use as turn-type prefix in history timeline.
 */
export const MiniDicePairIcon: React.FC<MiniDicePairIconProps> = memo(
  ({ color, className }) => (
    <svg viewBox="0 0 24 24" className={cn('h-6 w-6', className)} aria-hidden>
      {/* First die, top-left (outline only) */}
      <rect
        x={0}
        y={2}
        width={11}
        height={11}
        rx={2}
        fill="none"
        stroke={color}
        strokeWidth={1.2}
      />
      {/* Second die, bottom-right (outline only) */}
      <rect
        x={13}
        y={11}
        width={11}
        height={11}
        rx={2}
        fill="none"
        stroke={color}
        strokeWidth={1.2}
      />
    </svg>
  ),
  (prevProps, nextProps) =>
    prevProps.color === nextProps.color && prevProps.className === nextProps.className,
);

MiniDicePairIcon.displayName = 'MiniDicePairIcon';

interface MiniDiceGroupProps {
  /** Array of dice values (1-6) to display */
  values: number[];
  color: string;
  className?: string;
  /** When true, show diagonal cross-out on unselected dice (for SELECTDICESOPP) */
  selectedIndex?: number | null;
}

/**
 * Miniature dice group icon for timeline (multiple dice rolled).
 * Displays multiple dice diagonally offset, smaller than single dice.
 */
export const MiniDiceGroup: React.FC<MiniDiceGroupProps> = memo(
  ({ values, color, className, selectedIndex = null }) => {
    // For 2 dice: show both side-by-side horizontally with slight vertical offset for diagonal feel
    // For 3+ dice: show first 2 (most common case)
    const diceToShow = values.slice(0, 2);
    // Determine base size from className or use default
    // className format is typically "w-7 h-7" or "w-6 h-6"
    const baseSize = className || 'w-6 h-6';
    // Smaller size for grouped dice (about 55-60% of base, allowing slight overlap)
    // If base is w-7 h-7 (28px), small is w-4 h-4 (16px) = 57%
    // If base is w-6 h-6 (24px), small is w-3.5 h-3.5 (14px) = 58%
    const smallSize = baseSize.includes('w-7') ? 'w-4 h-4' : 'w-3.5 h-3.5';

    return (
      <div className={cn('relative', baseSize)} aria-hidden>
        {diceToShow.map((value, index) => {
          const isSelected = selectedIndex === null || index === selectedIndex;
          // Diagonal layout: first die at top-left, second at bottom-right
          // Slight overlap is okay - values are still distinguishable
          // Small offset allows minimal diagonal overlap
          const edgeOffset = index === 0 ? '0' : '0.125rem'; // First at corner, second offset by 2px
          return (
            <div
              key={index}
              className="absolute"
              style={{
                left: index === 0 ? '0' : 'auto',
                right: index === 0 ? 'auto' : edgeOffset,
                top: index === 0 ? '0' : 'auto',
                bottom: index === 0 ? 'auto' : edgeOffset,
                zIndex: index === 0 ? 1 : 2, // Second die on top
              }}
            >
              <MiniDiceIcon
                value={value}
                color={color}
                className={smallSize}
                disabled={selectedIndex !== null && !isSelected}
              />
            </div>
          );
        })}
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.values.length === nextProps.values.length &&
    prevProps.values.every((v, i) => v === nextProps.values[i]) &&
    prevProps.color === nextProps.color &&
    prevProps.className === nextProps.className &&
    prevProps.selectedIndex === nextProps.selectedIndex,
);

MiniDiceGroup.displayName = 'MiniDiceGroup';
